import { useState, useEffect, useRef, useCallback } from "react";
import { MagnifyingGlass, Funnel, X, CalendarBlank as CalendarIcon, CaretDown, Storefront, CurrencyDollar, Users, CaretUp, Crosshair, MapPin, MagnifyingGlass as SearchIcon, MapPinLine, Flag, CalendarDots, Hash } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { format } from "date-fns";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FilterBar = ({ filters, onFilterChange, onClearFilters, states, categories, nearMeActive, nearMeLoading, nearMeRadius, onActivateNearMe, onDeactivateNearMe, onRadiusChange, nearMeCount }) => {
  const [searchValue, setSearchValue] = useState(filters.search || "");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [activeShowType, setActiveShowType] = useState(null);
  const debounceRef = useRef(null);
  const acDebounceRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchContainerRef = useRef(null);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/autocomplete`, { params: { q: query } });
      setSuggestions(res.data.suggestions || []);
      setShowSuggestions(true);
      setSelectedIdx(-1);
    } catch (error) {
      console.error("Autocomplete failed:", error.message);
    }
  }, []);

  // Show types sorted alphabetically
  const showTypes = [
    { label: "Anime", search: "anime" },
    { label: "Antiques & Flea", search: "antique OR flea OR vintage OR thrift" },
    { label: "Art & Design", search: "art walk OR street art OR mural OR photography OR sculpture" },
    { label: "Asian Cultural", search: "asian OR lunar OR diwali OR cherry blossom" },
    { label: "Bike Shows", search: "bike" },
    { label: "Black Cultural", search: "juneteenth OR african OR gospel OR hbcu OR soul food" },
    { label: "Car Shows", search: "car show" },
    { label: "Craft Shows", search: "craft" },
    { label: "Fairs", search: "fair" },
    { label: "Festivals", search: "festival" },
    { label: "Food & Drink", search: "seafood OR wing OR pizza OR chocolate OR coffee OR taco OR whiskey OR donut OR barbecue OR mac cheese" },
    { label: "Health & Wellness", search: "yoga OR wellness OR fitness OR health" },
    { label: "Holiday & Seasonal", search: "christmas OR 4th of july OR halloween OR easter OR thanksgiving OR valentine" },
    { label: "Kids", search: "kids" },
    { label: "Latino/Hispanic", search: "latino OR fiesta OR dia de los" },
    { label: "LGBTQ+ Pride", search: "pride" },
    { label: "Music", search: "music OR jazz OR blues OR reggae OR bluegrass OR hip hop OR country music" },
    { label: "Pets & Animals", search: "dog show OR pet OR cat show OR equestrian OR reptile" },
    { label: "Sports & Outdoor", search: "rodeo OR fishing OR marathon OR surf OR outdoor adventure OR golf" },
    { label: "State & County Fairs", search: "state fair OR county fair OR agricultural OR livestock OR harvest" },
    { label: "Tech & Maker", search: "tech expo OR maker OR startup OR drone OR gaming" },
  ];

  const handleShowTypeClick = (showType) => {
    if (activeShowType === showType.label) {
      // Deselect
      setActiveShowType(null);
      setSearchValue("");
      onFilterChange({ search: "" });
    } else {
      setActiveShowType(showType.label);
      setSearchValue(showType.search);
      onFilterChange({ search: showType.search });
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onFilterChange({ search: searchValue });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value === "") {
      setActiveShowType(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
    // Live search: debounce 300ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({ search: value });
    }, 300);
    // Autocomplete: debounce 200ms, trigger at 3+ chars
    if (acDebounceRef.current) clearTimeout(acDebounceRef.current);
    acDebounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 200);
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    if (suggestion.type === "state") {
      setSearchValue("");
      onFilterChange({ state: suggestion.value, search: "" });
    } else if (suggestion.type === "city") {
      setSearchValue(suggestion.value);
      if (suggestion.state) {
        onFilterChange({ search: suggestion.value, state: suggestion.state });
      } else {
        onFilterChange({ search: suggestion.value });
      }
    } else if (suggestion.type === "zipcode") {
      setSearchValue(suggestion.value);
      if (suggestion.state) {
        onFilterChange({ search: suggestion.value, state: suggestion.state });
      } else {
        onFilterChange({ search: suggestion.value });
      }
    } else if (suggestion.type === "event") {
      setSearchValue(suggestion.value);
      onFilterChange({ search: suggestion.value });
    }
  };

  const handleSearchKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && selectedIdx >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedIdx]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  // Cleanup debounces on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (acDebounceRef.current) clearTimeout(acDebounceRef.current);
    };
  }, []);

  const getCategoryLabel = (category) => {
    const labels = {
      festival: "Festivals",
      fair: "Fairs",
      community: "Community Events"
    };
    return labels[category] || category;
  };

  const getBoothFeeLabel = (range) => {
    const labels = {
      "0-100": "Under $100",
      "100-300": "$100 - $300",
      "300-500": "$300 - $500",
      "500+": "$500+"
    };
    return labels[range] || "Any Price";
  };

  const getAttendanceLabel = (range) => {
    const labels = {
      "0-1000": "Under 1K",
      "1000-5000": "1K - 5K",
      "5000-10000": "5K - 10K",
      "10000+": "10K+"
    };
    return labels[range] || "Any Size";
  };

  const hasActiveFilters = filters.category || filters.state || filters.search || 
    filters.startDate || filters.endDate || filters.applicationsOpen || filters.boothFeeRange || filters.attendanceRange;

  return (
    <div 
      className="bg-white rounded-xl border border-[#D6D3D1] p-3 sm:p-4 md:p-5 shadow-sm"
      data-testid="filter-bar"
    >
      <div className="flex flex-col gap-3 sm:gap-4">
        {/* Top Row: Search + Main Filters */}
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
          {/* Search Input with Autocomplete */}
          <form onSubmit={handleSearchSubmit} className="flex-1" ref={searchContainerRef}>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass 
                  weight="regular" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C757D] z-10" 
                />
                <Input
                  type="text"
                  placeholder="Search by city, state, zip, or event name..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                  className="pl-10 pr-4 h-10 sm:h-11 bg-[#F7F5F0] border-[#D6D3D1] focus:border-[#E07A5F] focus:ring-[#E07A5F]/20 rounded-lg text-sm sm:text-base"
                  data-testid="search-location-input"
                  autoComplete="off"
                />

                {/* Autocomplete Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#D6D3D1] rounded-xl shadow-xl z-50 max-h-[360px] overflow-y-auto"
                    data-testid="autocomplete-dropdown"
                  >
                    {suggestions.map((s, i) => {
                      const icon = s.type === "state" ? <Flag weight="duotone" className="w-4 h-4 text-[#E07A5F]" />
                        : s.type === "city" ? <MapPinLine weight="duotone" className="w-4 h-4 text-[#81B29A]" />
                        : s.type === "zipcode" ? <Hash weight="duotone" className="w-4 h-4 text-[#3D405B]" />
                        : <CalendarDots weight="duotone" className="w-4 h-4 text-[#6C757D]" />;

                      const typeLabel = s.type === "state" ? "State"
                        : s.type === "city" ? "City"
                        : s.type === "zipcode" ? "Zip Code"
                        : "Event";

                      return (
                        <button
                          key={`${s.type}-${s.value}-${i}`}
                          type="button"
                          onClick={() => handleSuggestionClick(s)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                            i === selectedIdx ? "bg-[#E07A5F]/10" : "hover:bg-[#F7F5F0]"
                          } ${i === 0 ? "rounded-t-xl" : ""} ${i === suggestions.length - 1 ? "rounded-b-xl" : ""}`}
                          data-testid={`suggestion-${s.type}-${i}`}
                        >
                          {icon}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-[#2B2D42] block truncate">{s.label}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {s.count && (
                              <span className="text-[10px] text-[#6C757D] bg-[#F7F5F0] px-1.5 py-0.5 rounded-full">
                                {s.count} shows
                              </span>
                            )}
                            <span className="text-[10px] font-medium text-[#A8A5A1] uppercase">{typeLabel}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <Button
                type="submit"
                className="h-10 sm:h-11 px-3 bg-[#E07A5F] hover:bg-[#D46A50] text-white rounded-lg font-medium text-xs flex-shrink-0"
                data-testid="search-submit-btn"
              >
                Enter
              </Button>
            </div>
          </form>

          {/* Filter Controls - scroll horizontally on mobile */}
          <div className="flex flex-nowrap sm:flex-wrap gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
            {/* Category Filter */}
            <Select 
              value={filters.category || "all"} 
              onValueChange={(value) => onFilterChange({ category: value === "all" ? "" : value })}
            >
              <SelectTrigger 
                className="w-[130px] sm:w-[150px] h-10 sm:h-11 bg-[#F7F5F0] border-[#D6D3D1] rounded-lg flex-shrink-0 text-sm"
                data-testid="category-filter-dropdown"
              >
                <Funnel weight="duotone" className="w-4 h-4 mr-1 sm:mr-2 text-[#6C757D]" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="category-filter-all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem 
                    key={cat} 
                    value={cat}
                    data-testid={`category-filter-${cat}`}
                  >
                    {getCategoryLabel(cat)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* State Filter */}
            <Select 
              value={filters.state || "all"} 
              onValueChange={(value) => onFilterChange({ state: value === "all" ? "" : value })}
            >
              <SelectTrigger 
                className="w-[130px] sm:w-[150px] h-10 sm:h-11 bg-[#F7F5F0] border-[#D6D3D1] rounded-lg flex-shrink-0 text-sm"
                data-testid="state-filter-dropdown"
              >
                <SelectValue placeholder="State" />
                <CaretDown weight="bold" className="w-4 h-4 ml-auto text-[#6C757D]" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all" data-testid="state-filter-all">All States</SelectItem>
                {states.map((state) => (
                  <SelectItem 
                    key={state} 
                    value={state}
                    data-testid={`state-filter-${state.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Range - Start */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[110px] sm:w-[130px] h-10 sm:h-11 justify-start text-left font-normal bg-[#F7F5F0] border-[#D6D3D1] rounded-lg hover:bg-[#E8E6E1] flex-shrink-0 text-sm"
                  data-testid="filter-date-picker-start"
                >
                  <CalendarIcon weight="duotone" className="mr-1 sm:mr-2 h-4 w-4 text-[#6C757D]" />
                  {filters.startDate ? (
                    <span className="text-[#2B2D42]">{format(new Date(filters.startDate), "MMM d")}</span>
                  ) : (
                    <span className="text-[#6C757D]">Start</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.startDate ? new Date(filters.startDate) : undefined}
                  onSelect={(date) => onFilterChange({ 
                    startDate: date ? format(date, "yyyy-MM-dd") : null 
                  })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Date Range - End */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[110px] sm:w-[130px] h-10 sm:h-11 justify-start text-left font-normal bg-[#F7F5F0] border-[#D6D3D1] rounded-lg hover:bg-[#E8E6E1] flex-shrink-0 text-sm"
                  data-testid="filter-date-picker-end"
                >
                  <CalendarIcon weight="duotone" className="mr-1 sm:mr-2 h-4 w-4 text-[#6C757D]" />
                  {filters.endDate ? (
                    <span className="text-[#2B2D42]">{format(new Date(filters.endDate), "MMM d")}</span>
                  ) : (
                    <span className="text-[#6C757D]">End</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.endDate ? new Date(filters.endDate) : undefined}
                  onSelect={(date) => onFilterChange({ 
                    endDate: date ? format(date, "yyyy-MM-dd") : null 
                  })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Near Me Button & Radius Control */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3" data-testid="near-me-section">
          <Button
            onClick={nearMeActive ? onDeactivateNearMe : onActivateNearMe}
            disabled={nearMeLoading}
            className={`h-10 sm:h-11 px-4 sm:px-5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              nearMeActive
                ? "bg-[#E07A5F] hover:bg-[#D46A50] text-white shadow-md"
                : "bg-white hover:bg-[#F7F5F0] text-[#2B2D42] border border-[#D6D3D1]"
            }`}
            data-testid="near-me-btn"
          >
            <Crosshair weight="bold" className={`w-5 h-5 ${nearMeLoading ? "animate-spin" : ""}`} />
            {nearMeLoading ? "Locating..." : nearMeActive ? `Near Me (${nearMeCount})` : "Find Shows Near Me"}
          </Button>
          
          {nearMeActive && (
            <div className="flex items-center gap-3 bg-[#F7F5F0] rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 flex-1 sm:flex-initial sm:min-w-[280px]">
              <MapPin weight="duotone" className="w-4 h-4 text-[#E07A5F] flex-shrink-0" />
              <div className="flex-1 flex items-center gap-2 sm:gap-3">
                <span className="text-xs text-[#6C757D] whitespace-nowrap font-medium">{nearMeRadius} mi</span>
                <Slider
                  value={[nearMeRadius]}
                  onValueChange={(val) => onRadiusChange(val[0])}
                  min={10}
                  max={500}
                  step={10}
                  className="flex-1"
                  data-testid="near-me-radius-slider"
                />
                <span className="text-xs text-[#6C757D] whitespace-nowrap">500 mi</span>
              </div>
            </div>
          )}
        </div>

        {/* Show Type Quick Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:mx-0 sm:px-0" data-testid="show-type-filters">
          <span className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold whitespace-nowrap flex-shrink-0">
            Show Type:
          </span>
          {showTypes.map((type) => (
            <button
              key={type.label}
              onClick={() => handleShowTypeClick(type)}
              className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-all border flex-shrink-0 ${
                activeShowType === type.label
                  ? "bg-[#E07A5F] text-white border-[#E07A5F] shadow-sm"
                  : "bg-white text-[#2B2D42] border-[#D6D3D1] hover:border-[#E07A5F] hover:text-[#E07A5F]"
              }`}
              data-testid={`show-type-${type.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Vendor Filters - collapsible on mobile */}
        <div className="border-t border-[#D6D3D1]/50 pt-3">
          {/* Toggle for mobile */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex sm:hidden items-center justify-between w-full text-xs text-[#6C757D] uppercase tracking-wider font-semibold mb-2"
            data-testid="vendor-filters-toggle"
          >
            <span>Vendor Filters</span>
            {filtersExpanded ? (
              <CaretUp weight="bold" className="w-4 h-4" />
            ) : (
              <CaretDown weight="bold" className="w-4 h-4" />
            )}
          </button>
          
          {/* Desktop label */}
          <div className="hidden sm:flex flex-wrap items-center gap-4">
            <span className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold">
              Vendor Filters:
            </span>
            
            {/* Applications Open Toggle */}
            <div className="flex items-center gap-2 bg-[#F7F5F0] rounded-lg px-3 py-2">
              <Storefront weight="duotone" className="w-4 h-4 text-[#81B29A]" />
              <span className="text-sm text-[#2B2D42]">Applications Open</span>
              <Switch
                checked={filters.applicationsOpen}
                onCheckedChange={(checked) => onFilterChange({ applicationsOpen: checked })}
                data-testid="applications-open-toggle"
              />
            </div>

            {/* Booth Fee Range */}
            <Select 
              value={filters.boothFeeRange || "any"} 
              onValueChange={(value) => onFilterChange({ boothFeeRange: value === "any" ? "" : value })}
            >
              <SelectTrigger 
                className="w-[150px] h-10 bg-[#F7F5F0] border-[#D6D3D1] rounded-lg"
                data-testid="booth-fee-filter"
              >
                <CurrencyDollar weight="duotone" className="w-4 h-4 mr-2 text-[#81B29A]" />
                <SelectValue placeholder="Booth Fee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Booth Fee</SelectItem>
                <SelectItem value="0-100">Under $100</SelectItem>
                <SelectItem value="100-300">$100 - $300</SelectItem>
                <SelectItem value="300-500">$300 - $500</SelectItem>
                <SelectItem value="500+">$500+</SelectItem>
              </SelectContent>
            </Select>

            {/* Attendance Range */}
            <Select 
              value={filters.attendanceRange || "any"} 
              onValueChange={(value) => onFilterChange({ attendanceRange: value === "any" ? "" : value })}
            >
              <SelectTrigger 
                className="w-[150px] h-10 bg-[#F7F5F0] border-[#D6D3D1] rounded-lg"
                data-testid="attendance-filter"
              >
                <Users weight="duotone" className="w-4 h-4 mr-2 text-[#E07A5F]" />
                <SelectValue placeholder="Attendance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Attendance</SelectItem>
                <SelectItem value="0-1000">Under 1,000</SelectItem>
                <SelectItem value="1000-5000">1,000 - 5,000</SelectItem>
                <SelectItem value="5000-10000">5,000 - 10,000</SelectItem>
                <SelectItem value="10000+">10,000+</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={onClearFilters}
                className="h-10 px-4 text-[#E07A5F] hover:text-[#D46A50] hover:bg-[#E07A5F]/10 ml-auto"
                data-testid="clear-filters-btn"
              >
                <X weight="bold" className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          {/* Mobile vendor filters - collapsible */}
          <div className={`sm:hidden flex flex-col gap-3 ${filtersExpanded ? "block" : "hidden"}`}>
            {/* Applications Open Toggle */}
            <div className="flex items-center justify-between bg-[#F7F5F0] rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Storefront weight="duotone" className="w-4 h-4 text-[#81B29A]" />
                <span className="text-sm text-[#2B2D42]">Applications Open</span>
              </div>
              <Switch
                checked={filters.applicationsOpen}
                onCheckedChange={(checked) => onFilterChange({ applicationsOpen: checked })}
                data-testid="applications-open-toggle-mobile"
              />
            </div>

            <div className="flex gap-2">
              {/* Booth Fee Range */}
              <Select 
                value={filters.boothFeeRange || "any"} 
                onValueChange={(value) => onFilterChange({ boothFeeRange: value === "any" ? "" : value })}
              >
                <SelectTrigger 
                  className="flex-1 h-10 bg-[#F7F5F0] border-[#D6D3D1] rounded-lg text-sm"
                  data-testid="booth-fee-filter-mobile"
                >
                  <CurrencyDollar weight="duotone" className="w-4 h-4 mr-1 text-[#81B29A]" />
                  <SelectValue placeholder="Booth Fee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Booth Fee</SelectItem>
                  <SelectItem value="0-100">Under $100</SelectItem>
                  <SelectItem value="100-300">$100 - $300</SelectItem>
                  <SelectItem value="300-500">$300 - $500</SelectItem>
                  <SelectItem value="500+">$500+</SelectItem>
                </SelectContent>
              </Select>

              {/* Attendance Range */}
              <Select 
                value={filters.attendanceRange || "any"} 
                onValueChange={(value) => onFilterChange({ attendanceRange: value === "any" ? "" : value })}
              >
                <SelectTrigger 
                  className="flex-1 h-10 bg-[#F7F5F0] border-[#D6D3D1] rounded-lg text-sm"
                  data-testid="attendance-filter-mobile"
                >
                  <Users weight="duotone" className="w-4 h-4 mr-1 text-[#E07A5F]" />
                  <SelectValue placeholder="Attendance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Attendance</SelectItem>
                  <SelectItem value="0-1000">Under 1,000</SelectItem>
                  <SelectItem value="1000-5000">1,000 - 5,000</SelectItem>
                  <SelectItem value="5000-10000">5,000 - 10,000</SelectItem>
                  <SelectItem value="10000+">10,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters on mobile */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={onClearFilters}
                className="h-10 text-[#E07A5F] hover:text-[#D46A50] hover:bg-[#E07A5F]/10 w-full"
                data-testid="clear-filters-btn-mobile"
              >
                <X weight="bold" className="w-4 h-4 mr-1" />
                Clear All Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-[#D6D3D1]/50">
          <span className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold py-1 w-full sm:w-auto">
            Active:
          </span>
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#E07A5F]/10 text-[#E07A5F] rounded-full text-sm">
              "{filters.search}"
              <button 
                onClick={() => { setSearchValue(""); onFilterChange({ search: "" }); }}
                className="hover:bg-[#E07A5F]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.category && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#81B29A]/10 text-[#6A9A83] rounded-full text-sm">
              {getCategoryLabel(filters.category)}
              <button 
                onClick={() => onFilterChange({ category: "" })}
                className="hover:bg-[#81B29A]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.state && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#2B2D42]/10 text-[#2B2D42] rounded-full text-sm">
              {filters.state}
              <button 
                onClick={() => onFilterChange({ state: "" })}
                className="hover:bg-[#2B2D42]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.applicationsOpen && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#81B29A]/10 text-[#6A9A83] rounded-full text-sm">
              Apps Open
              <button 
                onClick={() => onFilterChange({ applicationsOpen: false })}
                className="hover:bg-[#81B29A]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.boothFeeRange && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#81B29A]/10 text-[#6A9A83] rounded-full text-sm">
              {getBoothFeeLabel(filters.boothFeeRange)}
              <button 
                onClick={() => onFilterChange({ boothFeeRange: "" })}
                className="hover:bg-[#81B29A]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.attendanceRange && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#E07A5F]/10 text-[#E07A5F] rounded-full text-sm">
              {getAttendanceLabel(filters.attendanceRange)} attendees
              <button 
                onClick={() => onFilterChange({ attendanceRange: "" })}
                className="hover:bg-[#E07A5F]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.startDate && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#E07A5F]/10 text-[#E07A5F] rounded-full text-sm">
              From: {format(new Date(filters.startDate), "MMM d")}
              <button 
                onClick={() => onFilterChange({ startDate: null })}
                className="hover:bg-[#E07A5F]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.endDate && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#E07A5F]/10 text-[#E07A5F] rounded-full text-sm">
              To: {format(new Date(filters.endDate), "MMM d")}
              <button 
                onClick={() => onFilterChange({ endDate: null })}
                className="hover:bg-[#E07A5F]/20 rounded-full p-0.5"
              >
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
